import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, ButtonIcon, Dropdown } from '@quintoandar-tokko/jopijs';
import styles from '../filterBar.module.scss';
import Trans from '@/components/shared/Trans';
import translations from '../FilterBar.trans.json';
import {
  normalizeText,
  sortByKeyEs,
  formatSelectedText,
  getIsAllSelected,
  toTeamScopeItem,
} from '../utils';
import { setSelectedIds } from '@/store/slices/components/filterBar/FilterBarSlice';
import FilterSelectControl, {
  useFilterSelection,
} from '../FilterSelectControl';
import { TEAM, TEAM_LABEL_KEY } from '../constants/filtersConstants';
import { getCompanyTeams } from '@/store/slices/components/filterBar/service';

export const MIN_TEAMS_TO_SHOW_DROPDOWN = 1;

function TeamsDropdown() {
  const dispatch = useDispatch();
  const { selectedIds, selectTeams } = useSelector(
    (state) => state.FilterBarSlice.filters
  );
  const [selectedLocalIds, setSelectedLocalIds] = useState([]);
  const [searchText, setSearchText] = useState('');

  const teamTitle = Trans(translations, 'Teams');
  const normalizedSearch = normalizeText(searchText);
  const filteredTeams = sortByKeyEs(
    selectTeams.filter((team) =>
      normalizeText(team[TEAM_LABEL_KEY]).includes(normalizedSearch)
    ),
    TEAM_LABEL_KEY
  );

  const toggleSelection = (team) => {
    setSelectedLocalIds((prev) => {
      const isSelected = prev.find((x) => x.id === team.id);
      if (isSelected) {
        return prev.filter((x) => x.id !== team.id);
      }
      return [...prev, { id: team.id, text: team.name, filter_type: TEAM }];
    });
  };

  const valueTextButton = formatSelectedText(selectedIds, teamTitle, TEAM);

  const handleTeamAdd = () => {
    dispatch(setSelectedIds({ idList: selectedLocalIds, type: TEAM }));
  };

  useEffect(() => {
    dispatch(getCompanyTeams());
  }, []);

  useEffect(() => {
    const teamsSelec = selectedIds.filter((id) => id.filter_type === TEAM);
    return setSelectedLocalIds(teamsSelec);
  }, [selectedIds]);

  const allTeamItems = selectTeams.map(toTeamScopeItem);

  const isAllSelected = getIsAllSelected(selectedIds, selectTeams, TEAM);
  const selection = useFilterSelection(TEAM, {
    allLabel: Trans(translations, 'AllTeams'),
    allSelected: isAllSelected,
  });
  const isAllLocalSelected = getIsAllSelected(
    selectedLocalIds,
    selectTeams,
    TEAM
  );

  const selectAll = () => {
    if (!selectTeams.length) return;

    if (isAllLocalSelected) {
      setSelectedLocalIds([]);
      return;
    }

    setSelectedLocalIds(allTeamItems);
  };

  // Per TBR-6201, the Team filter is only visible when the company has at
  // least one active team. Staying hidden while `selectTeams` is still
  // empty (both during the initial fetch and once it resolves with no
  // teams) avoids flashing an empty dropdown that then disappears.
  if (selectTeams.length < MIN_TEAMS_TO_SHOW_DROPDOWN) return null;

  return (
    <Dropdown height="auto" className={`dropdown ${styles.dropdownList}`}>
      <FilterSelectControl
        filled={selection.filled}
        pending={selectedLocalIds.length > 0}
        tooltip={selection.tooltip}
        onClear={selection.onClear}
        clearLabel={selection.clearLabel}
      >
        <Dropdown.Button
          variant="dropdownDefault"
          className={styles.dropdownButton}
          text={
            isAllSelected ? Trans(translations, 'AllTeams') : valueTextButton
          }
        />
      </FilterSelectControl>
      <Dropdown.Items clickClose height={336} className={styles.dropdownItems}>
        <Dropdown.Search
          placeholder={Trans(translations, 'SearchTeam')}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          suffix={
            searchText !== '' && (
              <ButtonIcon
                variant={['transparentIcon', 'iconExtraSmall22px']}
                icon="icon-cerrar"
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchText('');
                }}
              />
            )
          }
        />
        {!searchText && (
          <Dropdown.Multiselect
            key={`all-teams-${isAllLocalSelected}`}
            onClick={(e) => {
              e.stopPropagation();
              selectAll();
            }}
            active={isAllLocalSelected}
            className={styles.dropdownMultiselect}
          >
            {Trans(translations, 'AllTeams')}
          </Dropdown.Multiselect>
        )}
        {filteredTeams.map((team) => {
          const isActive = selectedLocalIds.find((x) => x.id === team.id);

          return (
            <Dropdown.Multiselect
              key={`${team.id}-${isActive ? '1' : '0'}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleSelection(team);
              }}
              active={isActive}
              className={styles.dropdownMultiselect}
            >
              {team.name}
            </Dropdown.Multiselect>
          );
        })}
        {filteredTeams.length === 0 && (
          <Dropdown.Text>{Trans(translations, 'NoResultsFound')}</Dropdown.Text>
        )}
        <Box className={styles.applyButtonContainer}>
          <Button
            className={styles.applyButton}
            variant={['primary', 'small']}
            onClick={handleTeamAdd}
          >
            {Trans(translations, 'Apply')}
          </Button>
        </Box>
      </Dropdown.Items>
    </Dropdown>
  );
}

export default TeamsDropdown;
